import React, { useCallback, useEffect, useState } from 'react';
import noop from 'lodash/noop';
import { useIntl } from 'react-intl-next';
import { useQueryParam } from 'react-resource-router';
import Picker, { countSingleOptions, formatLabel, PickerTrigger } from '@atlassian/paginated-picker';
import { TicketType } from '@atlassian/proforma-common-core/jira-common-models';
import { extractTicketTypeId } from '../../../../common/utils/ticket-types';
import { useProjectFormsController } from '../../../../controllers';
import { messages } from './messages';
const byFormNameAndId = (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
export const TicketTypeDropdown = ({
  isServiceProject,
  onChange
}) => {
  const [{
    forms,
    loading
  }] = useProjectFormsController();
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [typeIds] = useQueryParam('typeIds');
  const [typeOptions, setTypeOptions] = useState([]);
  useEffect(() => {
    if (!loading) {
      const typeFilter = isServiceProject ? t => t.type === TicketType.RequestType : t => t.type === TicketType.IssueType;
      const formTicketTypes = {};
      forms.forEach(item => item.requesttypes.filter(typeFilter).forEach(type => {
        const ticketTypeId = extractTicketTypeId(type.id);
        formTicketTypes[ticketTypeId] = {
          ...type,
          id: ticketTypeId
        };
      }));
      const sortedTicketTypes = Object.values(formTicketTypes).sort(byFormNameAndId).map(item => ({
        optionType: 'avatar',
        label: item.name,
        value: item.id,
        avatar: item.iconUrl
      }));
      setTypeOptions(sortedTicketTypes);
    }
  }, [forms, isServiceProject, loading]);
  const [selectedTypeOptions, setSelectedTypeOptions] = useState([]);
  const updateSelectedTypeOptions = useCallback(newTypeOptions => {
    setSelectedTypeOptions(newTypeOptions);
    onChange(newTypeOptions.map(option => option.value));
  }, [onChange]);
  const onLoadOptions = (search, a, b, c, d, callback) => {
    let optionsToDisplay = typeOptions;
    if (search) {
      const lowercaseSearch = search.toLocaleLowerCase();
      optionsToDisplay = typeOptions.filter(option => {
        var _option$label;
        return (_option$label = option.label) === null || _option$label === void 0 ? void 0 : _option$label.toLocaleLowerCase().includes(lowercaseSearch);
      });
    }
    callback({
      totalCount: countSingleOptions(optionsToDisplay),
      options: optionsToDisplay
    });
  };
  useEffect(() => {
    if (typeOptions !== null && typeOptions !== void 0 && typeOptions.length && typeIds) {
      const typeIdsArray = typeIds.split(',');
      const preSelectedTypeOptions = typeOptions.filter(option => 'value' in option && typeIdsArray.includes(option.value));
      if (preSelectedTypeOptions.length > 0) {
        updateSelectedTypeOptions(preSelectedTypeOptions);
      }
    }
  }, [typeOptions, typeIds, updateSelectedTypeOptions]);
  return /*#__PURE__*/React.createElement(Picker, {
    isOpen: isOpen,
    onOpen: noop,
    onClose: () => setIsOpen(false),
    value: selectedTypeOptions,
    fieldId: "proforma-ticket-type-dropdown",
    onChange: updateSelectedTypeOptions,
    onLoadOptions: onLoadOptions,
    onTrigger: ({
      ref
    }) => /*#__PURE__*/React.createElement(PickerTrigger, {
      hasValues: selectedTypeOptions.length > 0,
      onClick: () => setIsOpen(!isOpen),
      isSelected: isOpen,
      isInvalid: false,
      ref: ref
    }, formatLabel(intl.formatMessage(isServiceProject ? messages.requestType : messages.issueType), selectedTypeOptions)),
    onClearValues: () => updateSelectedTypeOptions([]),
    pagination: {
      enableFullList: false
    },
    ariaLabel: intl.formatMessage(isServiceProject ? messages.requestTypeAriaLabel : messages.issueTypeAriaLabel)
  });
};